import { Tag } from '@dev-spendesk/grapes';
import { useSelector } from 'react-redux';
import { Route } from 'react-router';

import { MultiUseCardsPage } from 'pages/MultiUseCards/MultiUseCardsPage';
import { HeaderWithNavigation } from 'src/core/common/components/HeaderWithNavigation/HeaderWithNavigation';
import { type NavigationLinkProps } from 'src/core/common/components/NavigationLink';
import { ProtectedRoute } from 'src/core/common/components/ProtectedRoute';
import { useFeature } from 'src/core/common/hooks/useFeature';
import { useTranslation } from 'src/core/common/hooks/useTranslation';
import FEATURES from 'src/core/constants/features';
import { routeFor, routes } from 'src/core/constants/routes';
import { useCompany } from 'src/core/modules/app/hooks/useCompany';
import { useUser } from 'src/core/modules/app/hooks/useUser';
import {
  CardsContainer,
  MyCardContainer,
} from 'src/core/modules/physical-cards';
import {
  getIsInvoiceToCardEnabled,
  getIsMultiUseVirtualCardsEnabled,
  getIsSingleUseCardsPageEnabled,
} from 'src/core/selectors/globalSelectors';

import { SingleUseCardsPage } from '../SingleUseCards/SingleUseCardsPage';
import { SubscriptionsPage } from '../subscriptions';

export const useAccessibleCardsLinks = (): NavigationLinkProps[] => {
  const company = useCompany();
  const user = useUser();
  const { t } = useTranslation('global');

  const hasPlasticCardsFeature = useFeature(FEATURES.PLASTIC_CARDS);
  const isMultiUseVirtualCardsEnabled = useSelector(
    getIsMultiUseVirtualCardsEnabled,
  );
  const isInvoiceToCardEnabled = useSelector(getIsInvoiceToCardEnabled);
  const isSingleUseCardsPageEnabled = useSelector(
    getIsSingleUseCardsPageEnabled,
  );

  if (company.churning_at) {
    return [];
  }

  const isMyCardAccessible = hasPlasticCardsFeature && user.has_plastic_card;
  const isSubscriptionAccessible = company.type !== 'branch_expense_entity';
  const isCardsAccessible =
    (user.is_account_owner || user.is_admin) && hasPlasticCardsFeature;

  const links = [];

  if (isCardsAccessible) {
    links.push({
      key: 'physical-cards',
      text: t('cards.physicalCardsTitle'),
      path: routeFor(routes.CARDS.path, {
        company: company.id,
      }),
    });
  }

  if (isSubscriptionAccessible) {
    links.push({
      key: 'subscriptions',
      text: t('misc.subscription_plural'),
      path: routeFor(routes.SUBSCRIPTIONS.path, {
        company: company.id,
      }),
    });
  }

  if (isMultiUseVirtualCardsEnabled) {
    links.push({
      key: 'multi-use-cards',
      text: t('misc.multi_use_card_plural'),
      path: routeFor(routes.MULTI_USE_CARDS.path, {
        company: company.id,
      }),
    });
  }

  if (isInvoiceToCardEnabled || isSingleUseCardsPageEnabled) {
    links.push({
      key: 'single-use-cards',
      text: t('misc.single_use_card_plural'),
      path: routeFor(routes.SINGLE_USE_CARDS.path, {
        company: company.id,
      }),
      rightAddon: (
        <Tag className="h-[20px]" variant="purple">
          {t('misc.new')}
        </Tag>
      ),
    });
  }

  if (isMyCardAccessible) {
    /**
     * If Physical cardS page isn't accessible, push Card page at the beginning.
     * In that case, the link is named "Physical Card". Otherwise, it's named "My Card"
     */
    if (!isCardsAccessible) {
      links.unshift({
        key: 'mycard',
        text: t('navigation.physical_card'),
        path: routeFor(routes.CARD.path, {
          company: company.id,
        }),
      });
    } else {
      links.push({
        key: 'mycard',
        text: t('cards.myCardTitle'),
        path: routeFor(routes.CARD.path, {
          company: company.id,
        }),
      });
    }
  }

  return links;
};

export const CardsPage = () => {
  const links = useAccessibleCardsLinks();
  const { t } = useTranslation('global');

  const hasAccessToSubscription = links.some(
    (link) => link.key === 'subscriptions',
  );
  const hasAccessToCards = links.some((link) => link.key === 'physical-cards');

  const hasAccessToMultiUseCards = links.some(
    (link) => link.key === 'multi-use-cards',
  );

  const hasAccessToSingleUseCards = links.some(
    (link) => link.key === 'single-use-cards',
  );

  return (
    <div className="page__container">
      <HeaderWithNavigation links={links}>
        {t('misc.card_plural')}
      </HeaderWithNavigation>
      <Route path={routes.CARD.path} component={MyCardContainer} />
      <ProtectedRoute
        path={routes.CARDS.path}
        isAccessAllowed={hasAccessToCards}
      >
        <CardsContainer />
      </ProtectedRoute>
      <ProtectedRoute
        path={routes.SUBSCRIPTIONS.path}
        isAccessAllowed={hasAccessToSubscription}
      >
        <SubscriptionsPage />
      </ProtectedRoute>
      <ProtectedRoute
        path={routes.MULTI_USE_CARDS.path}
        isAccessAllowed={hasAccessToMultiUseCards}
      >
        <MultiUseCardsPage />
      </ProtectedRoute>
      <ProtectedRoute
        path={routes.SINGLE_USE_CARDS.path}
        isAccessAllowed={hasAccessToSingleUseCards}
      >
        <SingleUseCardsPage />
      </ProtectedRoute>
    </div>
  );
};
